import { call, put, takeEvery, select } from 'redux-saga/effects';
import { getType } from 'typesafe-actions';

import { RootState } from '../reducers/rootReducer';
import { updatePickTour, setCommercialSkipItemsAction, setEditingOrders } from '../actions/activePickTour.actions';
import { setLoading } from '../actions/loading.actions';
import { setPickTourPage } from '../actions/pickTourPage.actions';
import { setErrorFallbackPageData } from '../actions/errorFallbackPage.actions';

import { mapPickTourDataToUpdatePickTourRequest } from './updatePickTour.mappers';
import { callAPI } from '../../utils';
import { handleApiErrors, catchApiErrors } from './utils';

import {
  SAGA_UPDATE_PICK_TOUR,
  BFF_V1,
  BFF_PICK_TOURS,
  PICK_TOUR_PAGE_COMPLETE,
  PICK_TOUR_PAGE_EXIT_REVIEW,
  PICK_TOUR_PAGE_PICK_TOUR,
  APP_PAGE_ORDER_LIST,
  APP_PAGE_ERROR_FALLBACK,
  PICK_TOUR_SUB_STATUS_PROGRESS,
  PICKING_APIGEE_BASE_PATH,
  SKIP_ITEMS_ACTION_EDIT_ORDER,
  SKIP_ITEMS_ACTION_FINALIZE,
  SKIP_ITEMS_ACTION_NONE,
  ASSIGNED_TO_DIFFERENT_USER_ERROR,
  UNASSIGNED_PICK_TOUR_ERROR,
  DUPLICATE_PICK_TOUR_ERROR,
} from '../../constants';

import { ConfigAuthValues } from '../../types';
import { UpdatePickTourRequest, SkipItemsAction } from './types';
import { ActivePickTourState } from '../reducers/types';
import { setAppPage } from '../actions/appPage.actions';

import { ActionTypes as MessagesActionTypes, Messages } from '../reducers/message.reducer';

const callUpdatePickTour = (data: UpdatePickTourRequest, configAuthValues: ConfigAuthValues) => {
  return callAPI({
    method: 'PUT',
    url: `${configAuthValues.APIGEE_BASE_URL}${PICKING_APIGEE_BASE_PATH}${BFF_V1}${BFF_PICK_TOURS}/${data.pickTourID}`,
    data,
  });
};

/**
 * Checks if error is acceptable for displaying complete pick tour page.
 * Acceptable errors indicate the operation already succeeded or is in an acceptable state.
 * These include: ORDER_CLOSED, PICKTOUR_CLOSED, PICKTOUR_ACTIVE
 */
const isAcceptableCompletionError = (error: any): boolean => {
  const ACCEPTABLE_ERROR_DETAILS = ['ORDER_CLOSED', 'PICKTOUR_CLOSED', 'PICKTOUR_ACTIVE'];
  const ACCEPTABLE_STATUS_CODES = [404, 400];

  return (
    !!error?.response?.data?.errors &&
    ACCEPTABLE_STATUS_CODES.includes(error?.response?.status) &&
    error.response.data.errors.some((e: { detail: string }) => ACCEPTABLE_ERROR_DETAILS.includes(e.detail))
  );
};

/**
 * Navigates user back to the order list page
 */
function* navigateToOrderList() {
  yield put(setAppPage({ name: APP_PAGE_ORDER_LIST }));
  yield put(setPickTourPage({ name: PICK_TOUR_PAGE_PICK_TOUR }));
}

/**
 * Handles pick tour completion - prints reports, sets commercial skip items action, navigates to complete page
 */
function* handlePickTourCompletion(pickTourType: string, editOrderEnabled: boolean, skipItemsAction?: string) {
  if (pickTourType === 'RECALL') {
    yield put({ type: MessagesActionTypes.SEND_MESSAGE, payload: Messages.PRINT_RECALL_REPORT });
  }
  if (pickTourType === 'OVERSTOCK') {
    yield put({ type: MessagesActionTypes.SEND_MESSAGE, payload: Messages.PRINT_DISCREPANCY_REPORT });
  }
  if (pickTourType === 'COMMERCIAL') {
    let action: SkipItemsAction = SKIP_ITEMS_ACTION_NONE;
    if (editOrderEnabled) {
      action = skipItemsAction === SKIP_ITEMS_ACTION_EDIT_ORDER ? SKIP_ITEMS_ACTION_EDIT_ORDER : SKIP_ITEMS_ACTION_FINALIZE;
    }
    yield put(setCommercialSkipItemsAction({ action }));
  }
  yield put(setPickTourPage({ name: PICK_TOUR_PAGE_COMPLETE }));
}

export function* runUpdatePickTourSaga(action: ReturnType<typeof updatePickTour>) {
  const configAuthValues = yield select((state: RootState) => state.config.authValues);
  const editOrderEnabled = yield select((state: RootState) => state.config.editOrderEnabled);

  const activePickTourFromState: ActivePickTourState = yield select((state: RootState) => state.activePickTour);
  if (activePickTourFromState) {
    yield put(setLoading({ sagaName: SAGA_UPDATE_PICK_TOUR, isLoading: true }));

    const { pickTourType } = mapPickTourDataToUpdatePickTourRequest(action.payload, activePickTourFromState.data);

    try {
      const result = yield call(
        callUpdatePickTour,
        mapPickTourDataToUpdatePickTourRequest(action.payload, activePickTourFromState.data),
        configAuthValues
      );

      // Handle editingOrders from the API response
      if (result?.data?.editingOrders) {
        yield put(setEditingOrders({ editingOrders: result.data.editingOrders }));
      }

      if (action.payload.updateType === 'finish') {
        yield call(handlePickTourCompletion, pickTourType, editOrderEnabled, action.payload.skipItemsAction);
      }
      if (action.payload.updateType === 'inactive') {
        yield call(navigateToOrderList);
      }
      if (action.payload.updateType === 'exit') {
        yield put(setPickTourPage({ name: PICK_TOUR_PAGE_EXIT_REVIEW }));
      }
      yield handleApiErrors(result?.data?.errors);
    } catch (error) {
      // If exit tour fails, still navigate to pending orders (escape route)
      if (action.payload.updateType === 'exit') {
        yield call(navigateToOrderList);
      }

      // Check if pick tour was unassigned from current user
      const errors = error?.response?.data?.errors;
      const isPickTourUnassignedError = errors?.some(
        (e: any) => [
          ASSIGNED_TO_DIFFERENT_USER_ERROR,
          UNASSIGNED_PICK_TOUR_ERROR,
          DUPLICATE_PICK_TOUR_ERROR
        ].includes(e.code)
      );

      // Set flag to prevent auto-fetch only for duplicate pick tour error
      const isDuplicatePickTourError = errors?.some((e: any) => e.code === DUPLICATE_PICK_TOUR_ERROR);
      if (isDuplicatePickTourError) {
        sessionStorage.setItem('SKIP_AUTO_FETCH_ORDERS', 'true');
      }

      if (action.payload.updateStatus.pickTourSubStatus === PICK_TOUR_SUB_STATUS_PROGRESS) {
        yield put(setAppPage({ name: APP_PAGE_ORDER_LIST }));
      }

      const isAcceptableFinishTourError = isAcceptableCompletionError(error);

      if (isAcceptableFinishTourError) {
        yield call(handlePickTourCompletion, pickTourType, editOrderEnabled, action.payload.skipItemsAction);
        yield put(setLoading({ sagaName: SAGA_UPDATE_PICK_TOUR, isLoading: false }));
        return;
      }

      // If finish tour fails (other errors), navigate to error fallback page
      // Skip error page for unassigned pick tour errors - let catchApiErrors show modal
      if (action.payload.updateType === 'finish' && !isPickTourUnassignedError) {
        yield put(
          setErrorFallbackPageData({
            errorMessage: 'ERRORS.UNABLE_TO_COMPLETE_PICK_TOUR',
            showIcon: true,
            iconType: 'warning',
          })
        );
        yield put(setAppPage({ name: APP_PAGE_ERROR_FALLBACK }));
        yield put(setLoading({ sagaName: SAGA_UPDATE_PICK_TOUR, isLoading: false }));
        return;
      }

      try {
        const data = mapPickTourDataToUpdatePickTourRequest(action.payload, activePickTourFromState.data);
        const url = `${PICKING_APIGEE_BASE_PATH}${BFF_V1}${BFF_PICK_TOURS}/${data.pickTourID}`;
        const method = 'PUT';
        const config = configAuthValues;
        yield catchApiErrors(error, { url, method, data, config, saga: SAGA_UPDATE_PICK_TOUR });

        // Only show error fallback page for non-unassigned errors
        if (!isPickTourUnassignedError) {
          yield put(
            setErrorFallbackPageData({
              showIcon: true,
              iconType: 'warning',
            })
          );
          yield put(setAppPage({ name: APP_PAGE_ERROR_FALLBACK }));
        }
      } catch (_error) {
        yield catchApiErrors(error);
      }
    }

    yield put(setLoading({ sagaName: SAGA_UPDATE_PICK_TOUR, isLoading: false }));
  }
}

export default function* watchUpdatePickTour() {
  yield takeEvery(getType(updatePickTour), runUpdatePickTourSaga);
}
